/*
 * Copyright (c) 2021, WSO2 Inc. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import React from "react";

import { Button, Typography } from '@material-ui/core';
import classNames from "classnames";

import { makeStyles } from "@material-ui/core/styles";

const useStyles = makeStyles((theme) => ({
    primaryBtn: {
        textTransform: "capitalize",
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        marginLeft: theme.spacing(2),
        fontSize: 12,
        boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06), 0 1px 2px 0 rgba(0,0,0,0.15)",
        "&:hover": {
            backgroundColor: "#5464D0",
            color: theme.palette.common.white,
            boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.06), 0 2px 4px 0 rgba(0,0,0,0.07)"
        },
        "&:focus": {
            backgroundColor: theme.palette.primary.main,
            background: "linear-gradient(180deg, rgba(0,0,0,0.07) 0%, rgba(0,0,0,0) 100%)",
            color: theme.palette.common.white,
            boxShadow: "inset 0 0 0 1px rgba(0,0,0,0.08), 0 0 0 0 rgba(0,0,0,0.07)"
        },
        "&:active": {
            backgroundColor: theme.palette.primary.main
        }
    },
    rounded: {
        height: theme.spacing(4.5),
        borderRadius: theme.spacing(2.2),
        paddingRight: theme.spacing(2.5)
    },
    disabled : {
        backgroundColor: "#D5D8EE !important",
        color: "#FFFFFF !important",
        boxShadow: "0 0 0 0 rgba(0,0,0,0.07)",
        '&:hover, &:active, &:focus': {
            boxShadow: "none"
        }
    },
    iconBtnText: {
        marginLeft: -theme.spacing(0.5),
        marginTop: -theme.spacing(0.25)
    },
    btnText: {
        marginTop: -theme.spacing(0.25)
    },
}));

export interface ButtonProps {
    children?: string;
    onClick: (param?: any) => void,
    text?: string;
    variant?: string;
    className?: string;
    role?: string;
    disableRipple?: boolean;
    disableFocusRipple?: boolean;
    startIcon?: JSX.Element;
    fullWidth?: boolean;
    disabled?: boolean;
    btnSize?: "medium" | "large" | "small";
    dataTestId?: string;
    id?: string;
}

export function PrimaryButtonRounded(props: ButtonProps) {
    const classes = useStyles();
    const { text, startIcon, onClick, fullWidth, disabled, className, btnSize, dataTestId, id } = props;
    return (
        <Button
            id={id}
            data-testid={dataTestId}
            onClick={onClick}
            variant="contained"
            classes={{
                root: classNames(classes.primaryBtn, classes.rounded),
                disabled: classes.disabled
            }}
            role="button"
            startIcon={startIcon}
            disableRipple={true}
            disableFocusRipple={true}
            fullWidth={fullWidth}
            disabled={disabled}
            className={className}
            size={btnSize}
        >
            {text ? (
                <Typography variant="body2" className={startIcon ? classes.iconBtnText : classes.btnText}>
                    {text}
                </Typography>
            ) : null}
        </Button>
    );
}
